import { Being } from "../models/Being";
import { Surroundings } from "../models/Surroundings";
import { landThresholds as thresholds, AI } from "../models/constants";
import type { IBeingState } from "./states/being/IBeingState";
import { DeadState } from "./states/being/DeadState";

export class BeingController {
    private readonly handlers: Map<string, IBeingState>;
    private readonly interrupts: Array<[string, IBeingState]>;

    constructor({ states }: { states: Map<string, IBeingState> }) {
        this.handlers = states;
        this.interrupts = [...states.entries()].filter(([, s]) => s.allowInterrupt);
    }

    tick(being: Being, surroundings: Surroundings): void {
        if (being.state === DeadState.stateName) return;

        this.updateImmunity(being, surroundings);

        if (!being.hasImmunity) {
            this.applyHealthDecay(being, surroundings.altitude);
        }

        if (being.health <= 0) {
            being.state = DeadState.stateName;
            return;
        }

        const interrupt = this.interrupts.find(([, s]) => s.canEnter(being, surroundings));
        if (interrupt) {
            being.state = interrupt[0];
        }

        const handler = this.handlers.get(being.state);
        if (handler) {
            being.state = handler.tick(being, surroundings);
        }
    }

    /**
     * Clears spawn immunity on the first tick where the being is out of danger.
     * Beings spawned in water or on mountains keep immunity until they reach
     * safe ground, so they cannot die before their flee has a chance to work.
     */
    private updateImmunity(being: Being, surroundings: Surroundings): void {
        if (being.hasImmunity && !surroundings.isInDanger) {
            being.hasImmunity = false;
        }
    }

    private applyHealthDecay(being: Being, altitude: number): void {
        let delta = -AI.HEALTH_DECAY_BASE;
        if (altitude < thresholds.sea) {
            delta = -AI.HEALTH_DECAY_WATER;
        } else if (altitude > thresholds.mountain) {
            delta = -AI.HEALTH_DECAY_MOUNTAIN;
        }
        being.setHealth(being.health + delta);
    }
}
